import React, { useEffect } from 'react';
import * as useAPI from 'hooks/useAPI';
import * as consts from 'config/consts';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';
import NumberLabel from 'components/features/utils/NumberLabel';
import { isIosAppNotPremium } from 'components/features/utils/userStatus';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import { isHiddenProductTypes } from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { getPositions } from 'config/api';
import { formatDate } from 'utils/formatter';
import { useGetTransactions } from 'hooks/useGetTransactions';
import { TransactionItem } from './TransactionItem';
import { PositionItem } from './PositionItem';

const DepositsAndWithdrawalsHistoryLoading = () => {
  return (
    <>
      <div className="pt-4 font-bold">--月--日の入出金</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
      <div className="text-xs mt-4 text-bluegray-500">
        ※入出金記録はMoneytree連携の口座が対象となります。みんかぶ連携口座の入出金記録は表示されません。
      </div>
    </>
  );
};

const StocksHistoryLoading = () => {
  return (
    <>
      <div className="pt-8 font-bold">保有銘柄の前日比</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
      <div className="text-xs mt-4 text-bluegray-500">
        ※外国株式の評価額は前営業日の終値に本日9:00（日本時間）時点の為替レートを掛けて算出しています。
      </div>
      <div className="text-xs mt-4 text-bluegray-500">
        ※一部の投資信託は連携後、1〜2営業日ほどデータ反映が遅れる可能性があります。
      </div>
    </>
  );
};

export const sortByNumber = (
  first: number | null,
  second: number | null
): number => {
  const firstNum = first || 0;
  const secondNum = second || 0;
  if (firstNum > secondNum) {
    return -1;
  } else if (firstNum < secondNum) {
    return 1;
  } else {
    return 0;
  }
};

const DepositsAndWithdrawalsHistory = ({
  targetDate,
  onAccountItemClick,
}: {
  targetDate: Date | null;
  onAccountItemClick: (account: string) => void;
}) => {
  const { transactions: transactions_, getTransactions } = useGetTransactions();

  useEffect(() => {
    getTransactions({ reportType: consts.reportType.日次 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (useAPI.isLoading(transactions_))
    return <DepositsAndWithdrawalsHistoryLoading />;

  const transactions = transactions_.transactions.items;
  transactions.sort((first, second) =>
    sortByNumber(first.amount, second.amount)
  );

  return (
    <>
      <div className="pt-4 font-bold">
        {targetDate && formatDate(targetDate, 'M月d日') + 'の入出金'}
      </div>
      <div className="border rounded-lg mt-2 overflow-hidden">
        <div className="bg-bluegray-50">
          <div className="flex mx-3 items-center">
            <div className="py-4 text-sm font-bold text-bluegray-500">
              入出金の合計
            </div>
            <div className="flex-grow flex justify-end">
              <NumberLabel
                value={transactions.reduce(function(sum, transaction) {
                  return sum + transaction.amount;
                }, 0)}
                unitName="円"
                className="font-bold"
                classNames={['text-xl', 'text-xs mt-1 ml-1']}
              />
            </div>
          </div>
        </div>
        <div className="border-t">
          {transactions.length ? (
            transactions.map((transaction, i) => {
              return (
                <TransactionItem
                  {...{
                    borderTop: i > 0 && <div className="border-b ml-3" />,
                    onAccountItemClick,
                    transaction,
                    key: i,
                  }}
                />
              );
            })
          ) : (
            <div className="flex items-center justify-center mx-3 py-4 text-sm text-bluegray-500">
              {targetDate &&
                formatDate(targetDate, 'M月d日') +
                  'に取得した出入金記録はありません'}
            </div>
          )}
        </div>
      </div>
      <div className="text-xs mt-4 text-bluegray-500">
        ※入出金記録はMoneytree連携の口座が対象となります。みんかぶ連携口座の入出金記録は表示されません。
      </div>
    </>
  );
};

const marginSortNum = 15;
const positionSortList = [
  {
    productType: consts.productTypes.国内株式,
    sortNum: 1,
  },
  {
    productType: consts.productTypes.未上場株式,
    sortNum: 2,
  },
  {
    productType: consts.productTypes.投資信託,
    sortNum: 3,
  },
  {
    productType: consts.productTypes.外国株式,
    sortNum: 4,
  },
  {
    productType: consts.productTypes['国内ETF・ETN'],
    sortNum: 5,
  },
  {
    productType: consts.productTypes['外国ETF・ETN'],
    sortNum: 6,
  },
  {
    productType: consts.productTypes.国内REIT,
    sortNum: 7,
  },
  {
    productType: consts.productTypes.外国REIT,
    sortNum: 8,
  },
  {
    productType: consts.productTypes.国内債券,
    sortNum: 9,
  },
  {
    productType: consts.productTypes.外国債券,
    sortNum: 10,
  },
  {
    productType: consts.productTypes.国内EB,
    sortNum: 11,
  },
  {
    productType: consts.productTypes.外国EB,
    sortNum: 12,
  },
  {
    productType: consts.productTypes.不動産,
    sortNum: 13,
  },
  {
    productType: consts.productTypes.ロボアド,
    sortNum: 14,
  },
  {
    productType: consts.productTypes.預金_MRF,
    sortNum: 16,
  },
  {
    productType: consts.productTypes.その他,
    sortNum: 17,
  },
] as {
  productType: number;
  sortNum: number;
}[];

const StocksHistory = ({
  targetDate,
  onTradeItemClick,
}: {
  targetDate: Date | null;
  onTradeItemClick: (trade: string) => void;
}) => {
  const { data: positionsData } = useQuery('dailyPositions', fetchPositions, {
    refetchOnWindowFocus: false,
  });

  const positions_ = (positionsData as PositionItemsProps[]) || [];
  const positions = positions_.filter(
    position => !isHiddenProductTypes(position.productType)
  );

  positions.sort((first, second) =>
    sortByNumber(first.diffProfitOrLoss ?? 0, second.diffProfitOrLoss ?? 0)
  );

  positions.sort((first, second) => {
    if (first === undefined || second === undefined) return 0;

    const firstSortNum =
      first.cashMargin === consts.CashMargin.信用
        ? marginSortNum
        : positionSortList.find(list => list.productType === first.productType)
            ?.sortNum;

    const secondSortNum =
      second.cashMargin === consts.CashMargin.信用
        ? marginSortNum
        : positionSortList.find(list => list.productType === second.productType)
            ?.sortNum;

    if (firstSortNum === undefined || secondSortNum === undefined) return 0;

    if (firstSortNum > secondSortNum) {
      return 1;
    } else if (firstSortNum < secondSortNum) {
      return -1;
    } else {
      return 0;
    }
  });

  positions.sort((first, second) => {
    if (first.productType !== 1 || second.productType !== 1) return 0;
    if (first.cashMargin > second.cashMargin) {
      return 1;
    } else if (first.cashMargin < second.cashMargin) {
      return -1;
    } else {
      return 0;
    }
  });

  const isDiffProfitOrLossAllNull = positions.every(
    position => position.diffProfitOrLoss === null
  );
  const summaryDiffProfitOrLoss = positions.reduce(function(sum, position) {
    const diffProfitOrLoss = position.diffProfitOrLoss ?? 0;
    return sum + diffProfitOrLoss;
  }, 0);

  if (positionsData === null || positionsData === undefined)
    return <StocksHistoryLoading />;

  return (
    <>
      <div className={`${isIosAppNotPremium() ? 'pt-4' : 'pt-8'} font-bold`}>
        保有銘柄の前日比
      </div>
      <div className="border rounded-lg mt-2 overflow-hidden">
        <div className="bg-bluegray-50">
          <div className="flex mx-3 items-center">
            <div className="py-4 text-sm font-bold text-bluegray-500">
              保有銘柄の前日比
            </div>
            <div className="flex-grow flex justify-end">
              <NumberLabel
                value={
                  isDiffProfitOrLossAllNull ? null : summaryDiffProfitOrLoss
                }
                unitName="円"
                className="font-bold"
                classNames={['text-xl', 'text-xs mt-1 ml-1']}
                isDispalyNoValueUnit
              />
            </div>
          </div>
        </div>
        <div className="border-t">
          {positions.length ? (
            positions.map((position, i) => {
              return (
                <PositionItem
                  {...{
                    borderTop: i > 0 && <div className="border-b ml-3" />,
                    onTradeItemClick,
                    position,
                    key: i,
                  }}
                />
              );
            })
          ) : (
            <div className="flex items-center justify-center mx-3 py-4 text-sm text-bluegray-500">
              {targetDate &&
                formatDate(targetDate, 'M月d日') +
                  'に変動があった保有銘柄はありません'}
            </div>
          )}
        </div>
      </div>
      <div className="text-xs mt-4 text-bluegray-500">
        ※外国株式の評価額は前営業日の終値に本日9:00（日本時間）時点の為替レートを掛けて算出しています。
      </div>
      <div className="text-xs mt-4 text-bluegray-500">
        ※一部の投資信託は連携後、1〜2営業日ほどデータ反映が遅れる可能性があります。
      </div>
    </>
  );
};

type TransactionsHistoryProps = {
  onAccountItemClick: (account: string) => void;
  onTradeItemClick: (symbol: string) => void;
  targetDate: Date | null;
};

export const TransactionsHistory = ({
  onAccountItemClick,
  onTradeItemClick,
  targetDate,
}: TransactionsHistoryProps): React.ReactElement => {
  return (
    <>
      <div className="text-lg font-bold ml-4 mt-16 mb-1">入出金の履歴</div>
      <div className="bg-white">
        <div className="m-3 pb-4">
          {!isIosAppNotPremium() && (
            <DepositsAndWithdrawalsHistory
              {...{ targetDate, onAccountItemClick }}
            />
          )}
          <StocksHistory {...{ targetDate, onTradeItemClick }} />
        </div>
      </div>
    </>
  );
};

const fetchPositions = async () => {
  const res_ = await getPositions(undefined, consts.reportType.日次);
  const res = res_.Items.map(res => {
    return {
      brokerID: res.BrokerID,
      account: res.Account,
      symbol: res.Symbol,
      symbolName: res.SymbolName,
      accountType: res.AccountType,
      productType: res.ProductType,
      cashMargin: res.CashMargin,
      side: res.Side,
      qty: res.Qty,
      price: res.Price,
      currentAmount: res.CurrentAmount,
      currentAmountUSD: res.CurrentAmountUSD,
      profitOrLoss: res.ProfitOrLoss,
      profitOrLossUSD: res.ProfitOrLossUSD,
      validationAmount: res.ValidationAmount,
      diffProfitOrLoss: res.DiffProfitOrLoss,
      diffProfitOrLossUSD: res.DiffProfitOrLossUSD,
      currencyUnit: res.CurrencyUnit,
    };
  });
  return res;
};
